import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Send,
  Loader2,
  Save,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { DynamicFormRenderer } from '@/components/common/dynamic-form-renderer'
import { MerchantJourneyStepper } from '@/components/common/merchant-journey-stepper'
import { appRoutes } from '@/constants'
import { useCustomerFormConfig, useSubmitDealerRequest } from '../../hooks/use-customer'
import { api, unwrap } from '@/api'
import { toast } from 'sonner'

export function BecomeDealerPage() {
  const navigate = useNavigate()
  const { data: formConfigData, isLoading: isLoadingConfig } = useCustomerFormConfig('DEALER')
  const submitDealerMutation = useSubmitDealerRequest()

  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [remarks, setRemarks] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [existingRequest, setExistingRequest] = useState<any | null>(null)
  const [isDraftSaving, setIsDraftSaving] = useState(false)

  const fields = (formConfigData as any)?.fields || []
  const documents = (formConfigData as any)?.documents || []
  const formVersion = (formConfigData as any)?.version || 1
  const formConfigId = (formConfigData as any)?.id

  useEffect(() => {
    api
      .get('/platform/application/draft?type=DEALER')
      .then((res) => {
        const draft = unwrap(res)
        if (draft) {
          setExistingRequest(draft)
          if (draft.applicationData) {
            setFormValues(draft.applicationData)
          }
          if (draft.remarks) {
            setRemarks(draft.remarks)
          }
        }
      })
      .catch(() => {})
  }, [])

  const handleFieldValueChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSaveDraft = async () => {
    try {
      setIsDraftSaving(true)
      await api.post('/platform/application/save-draft', {
        requestType: 'DEALER',
        formConfigId,
        formVersion,
        formConfigSnapshot: formConfigData,
        applicationData: cleanApplicationData(formValues),
        uploadedDocuments: extractDocuments(formValues),
        remarks,
      })
      toast.success('Dealership application draft saved!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to save draft.')
    } finally {
      setIsDraftSaving(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed) {
      toast.error('Please agree to the dealer business terms.')
      return
    }

    const payload = {
      formConfigId,
      formVersion,
      formConfigSnapshot: formConfigData,
      applicationData: cleanApplicationData(formValues),
      uploadedDocuments: extractDocuments(formValues),
      remarks,
    }

    await submitDealerMutation.mutateAsync(payload)
    navigate(appRoutes.customer.approvalHistory)
  }

  const extractDocuments = (values: Record<string, any>) => {
    const docs: any[] = []
    Object.entries(values).forEach(([k, v]) => {
      if (v && typeof v === 'object' && v.fileName) {
        docs.push({ ...v, field: v.field || k })
      }
    })
    return docs
  }

  const cleanApplicationData = (values: Record<string, any>) => {
    const clean: Record<string, any> = {}
    Object.entries(values).forEach(([k, v]) => {
      if (v === null || v === undefined) return
      if (typeof v === 'object') return
      clean[k] = v
    })
    return clean
  }

  const isRejected = existingRequest?.status === 'REJECTED'

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Merchant Journey Stepper */}
      <MerchantJourneyStepper currentStep={existingRequest?.status === 'PENDING' ? 'PENDING_APPROVAL' : 'SUBMIT_APPLICATION'} />

      {/* Page Header */}
      <SectionHeading
        eyebrow="Enterprise Business Onboarding"
        title="Become an Authorized Dealership"
        description="For automotive showrooms, high-volume car dealers, and commercial spare part distributors. Unlock enterprise dealership quotas and priority buyer lead access."
        align="left"
      />

      {/* Rejection Notice Banner */}
      {isRejected && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-500 p-4 space-y-2">
          <div className="flex items-center gap-2 font-bold text-sm">
            <AlertTriangle className="size-5 shrink-0 text-red-500" />
            <span>Previous Application Rejection & Review Notes</span>
          </div>
          <p className="text-xs text-foreground/90 font-medium">
            {existingRequest?.reviewNotes || existingRequest?.remarks || 'Your previous application required updates. Please edit the form below and resubmit.'}
          </p>
        </Card>
      )}

      {/* Dynamic Application Form */}
      <Card className="border-slate-200 dark:border-purple-500/40 bg-white dark:bg-card shadow-md">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-border/60 pb-3">
            <div className="flex items-center gap-2">
              <Send className="size-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-display text-base font-bold text-slate-900 dark:text-foreground">
                {formConfigData?.title || 'Submit Dealership Application'}
              </h3>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={isDraftSaving}
              className="gap-1 text-xs font-bold"
            >
              <Save className="size-3.5" /> {isDraftSaving ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>

          {isLoadingConfig ? (
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Loading dealership form fields...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <DynamicFormRenderer
                fields={fields}
                documents={documents}
                values={formValues}
                onChange={handleFieldValueChange}
              />

              <div className="space-y-1.5 pt-3 border-t border-slate-200 dark:border-border/60">
                <label className="text-xs font-semibold text-slate-600 dark:text-muted-foreground">
                  Dealership Details & Showroom Remarks
                </label>
                <textarea
                  rows={4}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Include your showroom name, business registration number, city location, and estimated monthly vehicle inventory..."
                  className="w-full rounded-lg border border-slate-300 dark:border-border/80 bg-white dark:bg-background text-slate-900 dark:text-foreground placeholder:text-slate-400 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              <div className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-border/80 bg-slate-50 dark:bg-background/60 p-4">
                <input
                  type="checkbox"
                  id="agree-dealer"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 size-4 rounded border-slate-300 dark:border-border text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="agree-dealer" className="text-xs text-slate-600 dark:text-muted-foreground leading-relaxed cursor-pointer select-none">
                  I represent an authorized automotive business and agree to the <span className="font-bold text-slate-900 dark:text-foreground">Exotic Autocart Dealership Partner Terms</span>. All submitted documents are legitimate and verifiable.
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={submitDealerMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitDealerMutation.isPending || !agreed}
                  className="gap-2 font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-glow"
                >
                  {submitDealerMutation.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Submitting...
                    </>
                  ) : isRejected ? (
                    <>
                      <RotateCcw className="size-4" /> Resubmit Application
                    </>
                  ) : (
                    <>
                      <Send className="size-4" /> Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
